import { useEffect, Dispatch, SetStateAction, useContext } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import { CompanyPublicSettings } from "modules/company/types";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { IEvent } from "modules/event/types";
import { useSelector } from "react-redux";
import { selectIsAnonymousEnabled } from "modules/companySettings/redux/selectors";
import { selectShowUserPreferredLoginBypass } from "modules/auth/redux/selectors";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { NeedHelpChatButton } from "modules/helpChat/components/NeedHelpChatButton";
import { selectHasTickets } from "modules/ticketing/redux/selectors";
import { EmailInput } from "../types";
import { EmailOption } from "./EmailOption/EmailOption";
import { useStyles } from "./styles";
import { ThirdPartyOptions } from "./ThirdPartyOptions";

interface Props {
  eventCompanySettings: CompanyPublicSettings | undefined;
  hasCompanySettingsLoaded: boolean;
  event: IEvent;
  email: EmailInput;
  setEmail: Dispatch<SetStateAction<EmailInput>>;
  setGoToLogin: Dispatch<SetStateAction<boolean>>;
  setShowUserDetailsForm: Dispatch<SetStateAction<boolean>>;
  setShowUserNameForm: Dispatch<SetStateAction<boolean>>;
}

export const UserLoginOptions = ({
  eventCompanySettings,
  hasCompanySettingsLoaded,
  email,
  event,
  setEmail,
  setGoToLogin,
  setShowUserDetailsForm,
  setShowUserNameForm,
}: Props): JSX.Element => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["eventLanding", "common"]);
  const { checkIfEventHappeningNow } = useCheckEventStatus();
  const isHappeningNow = checkIfEventHappeningNow(event);
  const hasTickets = useSelector(selectHasTickets);
  const isAnonymousEnabled = useSelector(selectIsAnonymousEnabled);
  const showUserPreferredLoginBypass = useSelector(
    selectShowUserPreferredLoginBypass,
  );
  const styles = useStyles();
  const showAnonymousLoginOption =
    !event.isPrivate && isAnonymousEnabled && !hasTickets;

  const onAnonymousLoginClick = () => {
    track(Events.LOGIN_CLICKED_ANONYMOUS, {
      eventStatus: isHappeningNow ? "ongoing" : "upcoming",
    });

    setShowUserNameForm(true);
  };

  useEffect(() => {
    if (showUserPreferredLoginBypass) {
      track(Events.PREFERRED_LOGIN_FLOW_SHOWN);
    }
  }, [showUserPreferredLoginBypass, track]);

  return (
    <>
      <div className={styles.inputFormTitle}>
        {isHappeningNow ? (
          <Typography variant="h5">{t("eventLanding:join.event")}</Typography>
        ) : (
          <Typography variant="h4">{t("eventLanding:register.now")}</Typography>
        )}
      </div>

      {hasCompanySettingsLoaded && (
        <ThirdPartyOptions
          event={event}
          eventCompanySettings={eventCompanySettings}
          isEventHappeningNow={isHappeningNow}
          showAnonymousLoginOption={showAnonymousLoginOption}
          onAnonymousLoginClick={onAnonymousLoginClick}
        />
      )}
      {eventCompanySettings && (
        <EmailOption
          eventCompanySettings={eventCompanySettings}
          email={email}
          event={event}
          setEmail={setEmail}
          setGoToLogin={setGoToLogin}
          setShowUserDetailsForm={setShowUserDetailsForm}
          isHappeningNow={isHappeningNow}
        />
      )}

      <NeedHelpChatButton className={styles.supportButton} />
    </>
  );
};
